import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../cn.js';

export interface PopoverProps {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  children: (props: { close: () => void }) => ReactNode;
  align?: 'left' | 'right';
  className?: string;
}

const EDGE = 8;

/**
 * Anchored panel that closes on outside click or Escape. `align` picks which edge of the trigger
 * it lines up with; if it would still stick out of the window, it's nudged back inside.
 */
export function Popover({ trigger, children, align = 'right', className }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Purely visual nudge, applied straight to the panel before paint.
  useLayoutEffect(() => {
    const node = panel.current;
    if (!open || !node) return;
    const rect = node.getBoundingClientRect();
    const overflowRight = rect.right - (window.innerWidth - EDGE);
    const overflowLeft = EDGE - rect.left;
    let shift = 0;
    if (overflowRight > 0) shift = -Math.min(overflowRight, Math.max(0, rect.left - EDGE));
    else if (overflowLeft > 0) shift = overflowLeft;
    node.style.translate = shift ? `${shift}px 0` : '';
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {trigger({ open, toggle: () => setOpen((value) => !value) })}
      {open && (
        <div
          ref={panel}
          className={cn(
            'absolute top-full z-40 mt-2 animate-fade-in rounded-xl border border-border bg-surface shadow-float',
            align === 'right' ? 'right-0' : 'left-0',
            className,
          )}
        >
          {children({ close: () => setOpen(false) })}
        </div>
      )}
    </div>
  );
}
